/**
 * Position Form Component
 *
 * T033 [US1]: Reusable form component for adding/editing positions.
 * Provides form validation, error display, and user-friendly input handling.
 * NOW THEME-AWARE
 *
 * Updated to include:
 * - Asset category selection
 * - Sector/industry autocomplete (with manual entry option)
 * - Currency dropdown (from fx_rates + USD)
 * - Readonly fields when position is linked to instrument
 * - Compact layout
 * - Unsaved changes tracking
 */

import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { validateSymbol, validateNonNegativeNumber } from '../../utils/validation';
import { useTheme } from '../../contexts/ThemeContext';
import { AutocompleteInput } from '../ui/AutocompleteInput';
import { SymbolAutocomplete } from './SymbolAutocomplete';
import {
  getSectors,
  getIndustries,
  getCurrencies,
  SectorMaster,
  IndustryMaster,
} from '../../services/api/masterDataApi';
import {
  translateAssetCategory,
  translateSector,
  translateIndustry,
} from '../../utils/translationHelpers';

// Asset type options (hardcoded as per requirements)
const ASSET_TYPES = [
  { value: 'Stocks', label: 'Stocks' },
  { value: 'Bonds', label: 'Bonds' },
  { value: 'ETFs', label: 'ETFs' },
  { value: 'Funds', label: 'Funds' },
  { value: 'Commodities', label: 'Commodities' },
  { value: 'Crypto', label: 'Crypto' },
  { value: 'Others', label: 'Others' },
];

interface PositionFormProps {
  onSubmit: (
    symbol: string,
    quantity: number,
    cost_price: number,
    close_price?: number | null,
    currency: string,
    asset_type?: string,
    sector?: string | null,
    industry?: string | null
  ) => void;
  onFormChange?: (hasChanges: boolean) => void;
  loading?: boolean;
  initialValues?: {
    symbol: string;
    quantity: number;
    cost_price: number;
    close_price?: number | null;
    currency: string;
    asset_type?: string;
    sector?: string | null;
    industry?: string | null;
  } & {
    instrument_sector?: string;
    instrument_industry?: string;
  };
  submitButtonText?: string;
  readonlySymbol?: boolean; // T041 [US2]: Prevent editing symbol in edit mode
  readonlyFields?: string[]; // Fields that should be read-only (e.g., ['sector', 'industry', 'asset_type', 'currency'])
  hasInstrumentLink?: boolean;
  submitRef?: React.RefObject<{ triggerSubmit: () => void } | null>;
}

export interface PositionFormRef {
  triggerSubmit: () => void;
}

export const PositionForm = forwardRef<PositionFormRef, PositionFormProps>(
  (
    {
      onSubmit,
      onFormChange,
      loading = false,
      initialValues,
      submitButtonText,
      readonlySymbol = false,
      readonlyFields = [],
      hasInstrumentLink = false,
      submitRef,
    }: PositionFormProps,
    ref
  ) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const styles = useMemo(() => createThemedStyles(theme), [theme]);

    // Format number: remove trailing zeros, hide decimal if integer
    const formatNumber = (value: number | string | undefined): string => {
      if (value === undefined || value === null || value === '') return '';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) return String(value);
      // Remove trailing zeros and decimal point if not needed
      return parseFloat(num.toFixed(8)).toString();
    };

    const [symbol, setSymbol] = useState(initialValues?.symbol || '');
    const [quantity, setQuantity] = useState(formatNumber(initialValues?.quantity));
    const [costPrice, setCostPrice] = useState(formatNumber(initialValues?.cost_price));
    const [closePrice, setClosePrice] = useState(formatNumber(initialValues?.close_price));
    const [currency, setCurrency] = useState(initialValues?.currency || 'USD');
    const [assetType, setAssetCategory] = useState(initialValues?.asset_type || 'Stocks');
    const [sector, setSector] = useState(initialValues?.sector ?? '');
    const [industry, setIndustry] = useState(initialValues?.industry ?? '');

    // Track auto-filled values from instrument selection
    const [autoFilledValues, setAutoFilledValues] = useState<{
      currency?: string;
      sector?: string;
      industry?: string;
      asset_type?: string;
    }>({});

    // Track which fields were manually changed from auto-filled values
    const [userChangedFields, setUserChangedFields] = useState<Set<string>>(new Set());

    // Track pending industry value during auto-fill to prevent filter from clearing it
    const pendingIndustryRef = useRef<string | null>(null);

    // Track initial values for unsaved changes detection
    const initialValuesRef = useRef({
      symbol: initialValues?.symbol || '',
      quantity: formatNumber(initialValues?.quantity),
      cost_price: formatNumber(initialValues?.cost_price),
      close_price: formatNumber(initialValues?.close_price),
      currency: initialValues?.currency || 'USD',
      asset_type: initialValues?.asset_type || 'Stocks',
      sector: initialValues?.sector ?? '',
      industry: initialValues?.industry ?? '',
    });

    // Update initial values ref when initialValues prop changes
    useEffect(() => {
      initialValuesRef.current = {
        symbol: initialValues?.symbol || '',
        quantity: formatNumber(initialValues?.quantity),
        cost_price: formatNumber(initialValues?.cost_price),
        close_price: formatNumber(initialValues?.close_price),
        currency: initialValues?.currency || 'USD',
        asset_type: initialValues?.asset_type || 'Stocks',
        sector: initialValues?.sector ?? '',
        industry: initialValues?.industry ?? '',
      };
    }, [initialValues]);

    // Check for unsaved changes
    useEffect(() => {
      const hasChanges =
        symbol !== initialValuesRef.current.symbol ||
        quantity !== initialValuesRef.current.quantity ||
        costPrice !== initialValuesRef.current.cost_price ||
        closePrice !== initialValuesRef.current.close_price ||
        currency !== initialValuesRef.current.currency ||
        assetType !== initialValuesRef.current.asset_type ||
        sector !== initialValuesRef.current.sector ||
        industry !== initialValuesRef.current.industry;

      onFormChange?.(hasChanges);
    }, [
      symbol,
      quantity,
      costPrice,
      closePrice,
      currency,
      assetType,
      sector,
      industry,
      onFormChange,
    ]);

    // Master data
    const [sectors, setSectors] = useState<SectorMaster[]>([]);
    const [industries, setIndustries] = useState<IndustryMaster[]>([]);
    const [filteredIndustries, setFilteredIndustries] = useState<{ id: string; title: string }[]>(
      []
    );
    const [currencies, setCurrencies] = useState<string[]>([]);
    const [loadingMasterData, setLoadingMasterData] = useState(true);

    // UI state
    const [showAssetTypePicker, setShowAssetTypePicker] = useState(false);
    const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);

    // Store instrument sector/industry in refs to avoid dependency issues
    const instrumentSectorRef = useRef(initialValues?.instrument_sector);
    const instrumentIndustryRef = useRef(initialValues?.instrument_industry);

    // Update refs when initialValues change
    useEffect(() => {
      instrumentSectorRef.current = initialValues?.instrument_sector;
      instrumentIndustryRef.current = initialValues?.instrument_industry;
    }, [initialValues?.instrument_sector, initialValues?.instrument_industry]);

    // Sync sector/industry when initialValues change (e.g., when position loads)
    useEffect(() => {
      if (initialValues?.sector !== undefined) {
        setSector(initialValues.sector ?? '');
      }
      if (initialValues?.industry !== undefined) {
        setIndustry(initialValues.industry ?? '');
      }
    }, [initialValues?.sector, initialValues?.industry]);

    // Check if field should be readonly
    const isReadonly = (fieldName: string) => readonlyFields.includes(fieldName);

    const [errors, setErrors] = useState<{
      symbol?: string;
      quantity?: string;
      costPrice?: string;
      closePrice?: string;
      currency?: string;
    }>({});

    // Fetch master data on mount
    useEffect(() => {
      const fetchMasterData = async () => {
        try {
          const [sectorsData, industriesData, currenciesData] = await Promise.all([
            getSectors(),
            getIndustries(),
            getCurrencies(),
          ]);
          setSectors(sectorsData);
          setIndustries(industriesData);
          setCurrencies(currenciesData);

          // Set initial filtered industries (all industries if no sector selected)
          setFilteredIndustries(industriesData.map((i) => i.name));
        } catch (error) {
          console.error('Failed to fetch master data:', error);
          // Set fallback data
          setSectors([]);
          setIndustries([]);
          setFilteredIndustries([]);
          setCurrencies(['USD']);
        } finally {
          setLoadingMasterData(false);
        }
      };
      fetchMasterData();
    }, []);

    // Filter industries when sector changes or master data loads
    useEffect(() => {
      // Only filter if master data is loaded
      if (sectors.length === 0 || industries.length === 0) {
        return;
      }

      if (!sector) {
        // If no sector selected, show all industries
        setFilteredIndustries(
          industries.map((i) => ({ id: i.name, title: translateIndustry(i.name) }))
        );
        return;
      }

      // Find the selected sector to get its ID
      const selectedSector = sectors.find((s) => s.code === sector);
      if (selectedSector) {
        // Filter industries by sector_id
        const filtered = industries
          .filter((i) => i.sector_id === selectedSector.id)
          .map((i) => ({ id: i.name, title: translateIndustry(i.name) }));
        setFilteredIndustries(filtered);

        // Clear industry if it doesn't belong to the new sector
        // Skip this check if there's a pending industry value being set (during auto-fill)
        const industryToCheck = pendingIndustryRef.current || industry;
        if (industryToCheck && !filtered.find((f) => f.id === industryToCheck)) {
          // Only clear if there's no pending value AND current industry doesn't match
          if (!pendingIndustryRef.current) {
            setIndustry('');
          }
        }
      } else {
        // Custom sector not in list, show all industries
        setFilteredIndustries(
          industries.map((i) => ({ id: i.name, title: translateIndustry(i.name) }))
        );
      }
      // Note: 'industry' is intentionally excluded from deps to prevent re-filtering on every keystroke
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sector, sectors, industries]);

    const validateForm = (): boolean => {
      const newErrors: typeof errors = {};

      // Validate symbol
      const symbolValidation = validateSymbol(symbol);
      if (!symbolValidation.isValid) {
        newErrors.symbol = symbolValidation.errorMessage;
      }

      // Validate quantity (allow 0 for watchlist positions)
      const quantityValidation = validateNonNegativeNumber(quantity, 'Quantity');
      if (!quantityValidation.isValid) {
        newErrors.quantity = quantityValidation.errorMessage;
      }

      // Validate cost price (allow 0 for watchlist positions)
      const costPriceValidation = validateNonNegativeNumber(costPrice, 'Cost price');
      if (!costPriceValidation.isValid) {
        newErrors.costPrice = costPriceValidation.errorMessage;
      }

      // Validate close price (optional, allow 0 for delisted/worthless positions)
      if (closePrice && closePrice.trim()) {
        const closePriceValidation = validateNonNegativeNumber(closePrice, 'Current price');
        if (!closePriceValidation.isValid) {
          newErrors.closePrice = closePriceValidation.errorMessage;
        }
      }

      // Validate currency (should be selected from list)
      if (!currency || currency.length !== 3) {
        newErrors.currency = t('validation.invalidCurrency', { defaultValue: 'Invalid currency' });
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
      if (loading) return;

      if (validateForm()) {
        // Determine sector value to send:
        // - If field was auto-filled and user didn't change it, send undefined (use instrument value)
        // - If field was auto-filled and user changed it, send the new value
        // - If field was not auto-filled but has a value, send it
        // - If field is empty but originally had a value (user cleared it), send null to reset
        let sectorValue: string | null | undefined = sector || undefined;
        if (sector && autoFilledValues.sector === sector && !userChangedFields.has('sector')) {
          // Field still has auto-filled value and user didn't change it - don't override instrument
          sectorValue = undefined;
        } else if (!sector && initialValues?.sector) {
          sectorValue = null; // Explicitly clear the override
        }

        let industryValue: string | null | undefined = industry || undefined;
        if (
          industry &&
          autoFilledValues.industry === industry &&
          !userChangedFields.has('industry')
        ) {
          // Field still has auto-filled value and user didn't change it - don't override instrument
          industryValue = undefined;
        } else if (!industry && initialValues?.industry) {
          industryValue = null; // Explicitly clear the override
        }

        onSubmit(
          symbol.toUpperCase(),
          parseFloat(quantity),
          parseFloat(costPrice),
          closePrice && !isNaN(parseFloat(closePrice)) ? parseFloat(closePrice) : null,
          currency.toUpperCase(),
          assetType,
          sectorValue,
          industryValue
        );
      }
    };

    // Expose submit function to parent via ref
    useImperativeHandle(ref || submitRef, () => ({
      triggerSubmit: handleSubmit,
    }));

    if (loadingMasterData) {
      return (
        <View style={[styles.formContainer, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
              {t('common.loading', { defaultValue: 'Loading...' })}
            </Text>
          </View>
        </View>
      );
    }

    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[styles.container, { backgroundColor: theme.colors.surface }]}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {/* Symbol Input */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.symbol', { defaultValue: 'Symbol' })} *
            </Text>
            <SymbolAutocomplete
              value={symbol}
              onChangeText={(text) => {
                setSymbol(text);
                setErrors({ ...errors, symbol: undefined });
              }}
              placeholder={t('portfolio.symbolPlaceholder', { defaultValue: 'e.g., AAPL' })}
              error={errors.symbol}
              editable={!loading && !readonlySymbol}
              readOnly={readonlySymbol}
              onSymbolSelect={(option) => {
                setSymbol(option.symbol);
                setErrors({ ...errors, symbol: undefined });

                // Store auto-filled values and clear user-changed flags for these fields
                const newAutoFilledValues: typeof autoFilledValues = {};
                const newChangedFields = new Set(userChangedFields);

                // Set pending industry to prevent filter from clearing it during sector change
                if (option.industry) {
                  pendingIndustryRef.current = option.industry;
                }

                // Auto-fill currency
                if (option.currency) {
                  setCurrency(option.currency);
                  newAutoFilledValues.currency = option.currency;
                  newChangedFields.delete('currency');
                }

                // Auto-fill sector
                if (option.sector) {
                  setSector(option.sector);
                  newAutoFilledValues.sector = option.sector;
                  newChangedFields.delete('sector');
                }

                // Auto-fill industry
                if (option.industry) {
                  setIndustry(option.industry);
                  newAutoFilledValues.industry = option.industry;
                  newChangedFields.delete('industry');
                }

                // Auto-fill asset category
                if (option.asset_type) {
                  setAssetCategory(option.asset_type);
                  newAutoFilledValues.asset_type = option.asset_type;
                  newChangedFields.delete('asset_type');
                }

                setAutoFilledValues(newAutoFilledValues);
                setUserChangedFields(newChangedFields);

                // Clear pending industry ref after state updates are processed
                setTimeout(() => {
                  pendingIndustryRef.current = null;
                }, 0);
              }}
            />
          </View>

          {/* Quantity and Cost Price - Same Row */}
          <View style={styles.rowContainer}>
            <View style={[styles.fieldContainer, styles.halfField]}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.quantity', { defaultValue: 'Quantity' })} *
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: errors.quantity ? theme.colors.error : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                value={quantity}
                onChangeText={(text) => {
                  setQuantity(text);
                  setErrors({ ...errors, quantity: undefined });
                }}
                onBlur={() => setQuantity(formatNumber(quantity))}
                placeholder={t('portfolio.quantityPlaceholder', { defaultValue: 'e.g., 100' })}
                placeholderTextColor={theme.colors.textTertiary}
                keyboardType="decimal-pad"
                editable={!loading}
              />
              {errors.quantity ? (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.quantity}
                </Text>
              ) : null}
            </View>

            <View style={[styles.fieldContainer, styles.halfField]}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.costPrice', { defaultValue: 'Cost Price' })} *
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: errors.costPrice ? theme.colors.error : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                value={costPrice}
                onChangeText={(text) => {
                  setCostPrice(text);
                  setErrors({ ...errors, costPrice: undefined });
                }}
                onBlur={() => setCostPrice(formatNumber(costPrice))}
                placeholder={t('portfolio.costPricePlaceholder', { defaultValue: 'e.g., 150.75' })}
                placeholderTextColor={theme.colors.textTertiary}
                keyboardType="decimal-pad"
                editable={!loading}
              />
              {errors.costPrice ? (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.costPrice}
                </Text>
              ) : null}
            </View>
          </View>

          {/* Close Price - Full width, optional field */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.closePrice', { defaultValue: 'Close Price' })}
            </Text>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: errors.closePrice ? theme.colors.error : theme.colors.border,
                  color: theme.colors.text,
                  opacity: hasInstrumentLink ? 0.6 : 1,
                },
              ]}
              value={closePrice}
              onChangeText={(text) => {
                if (!hasInstrumentLink) {
                  setClosePrice(text);
                  setErrors({ ...errors, closePrice: undefined });
                }
              }}
              onBlur={() => setClosePrice(formatNumber(closePrice))}
              placeholder={t('portfolio.closePricePlaceholder', { defaultValue: 'e.g., 150.75' })}
              placeholderTextColor={theme.colors.textTertiary}
              keyboardType="decimal-pad"
              editable={!loading && !hasInstrumentLink}
            />
            {errors.closePrice ? (
              <Text style={[styles.errorText, { color: theme.colors.error }]}>
                {errors.closePrice}
              </Text>
            ) : null}
          </View>

          {/* Asset Category Dropdown */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.assetType', { defaultValue: 'Asset Category' })}
            </Text>
            <TouchableOpacity
              style={[
                styles.pickerInput,
                {
                  backgroundColor: isReadonly('asset_type')
                    ? theme.colors.background
                    : theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                  opacity: isReadonly('asset_type') ? 0.6 : 1,
                },
              ]}
              onPress={() => !isReadonly('asset_type') && setShowAssetTypePicker(true)}
              activeOpacity={0.7}
              disabled={loading || isReadonly('asset_type')}
            >
              <Text style={[styles.pickerText, { color: theme.colors.text }]}>
                {assetType
                  ? translateAssetCategory(assetType)
                  : t('portfolio.selectAssetCategory', { defaultValue: 'Select asset category' })}
              </Text>
              {!isReadonly('asset_type') ? (
                <Text style={[styles.pickerArrow, { color: theme.colors.textSecondary }]}>▼</Text>
              ) : null}
            </TouchableOpacity>

            {showAssetTypePicker ? (
              <View
                style={[
                  styles.pickerModal,
                  { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
                ]}
              >
                <ScrollView style={styles.pickerScroll} keyboardShouldPersistTaps="handled">
                  {ASSET_TYPES.map((cat) => (
                    <TouchableOpacity
                      key={cat.value}
                      style={[styles.pickerOption, { borderBottomColor: theme.colors.border }]}
                      onPress={() => {
                        setAssetCategory(cat.value);
                        setShowAssetTypePicker(false);
                        // Mark as user-changed if different from auto-filled value
                        if (
                          autoFilledValues.asset_type &&
                          cat.value !== autoFilledValues.asset_type
                        ) {
                          setUserChangedFields((prev) => new Set([...prev, 'asset_type']));
                        }
                      }}
                    >
                      <Text style={[styles.pickerOptionText, { color: theme.colors.text }]}>
                        {translateAssetCategory(cat.value)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <TouchableOpacity
                  style={[styles.pickerClose, { backgroundColor: theme.colors.background }]}
                  onPress={() => setShowAssetTypePicker(false)}
                >
                  <Text style={[styles.pickerCloseText, { color: theme.colors.text }]}>
                    {t('common.close', { defaultValue: 'Close' })}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>

          {/* Sector Autocomplete */}
          <View style={styles.fieldContainer}>
            <View style={styles.labelRow}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.sector', { defaultValue: 'Sector' })}
              </Text>
              {/* Show custom badge when linked and sector has value */}
              {hasInstrumentLink && sector ? (
                <View style={[styles.customBadge, { backgroundColor: theme.colors.primary }]}>
                  <Text style={[styles.customBadgeText, { color: theme.colors.textInverse }]}>
                    {t('portfolio.custom', { defaultValue: '自定义' })}
                  </Text>
                </View>
              ) : null}
            </View>
            <AutocompleteInput
              label=""
              value={sector}
              displayValue={sector ? translateSector(sector) : undefined}
              onChange={(value) => {
                setSector(value);
                // Mark as user-changed if different from auto-filled value
                if (autoFilledValues.sector && value !== autoFilledValues.sector) {
                  setUserChangedFields((prev) => new Set([...prev, 'sector']));
                }
              }}
              options={sectors.map((s) => ({ id: s.code, title: translateSector(s.code) }))}
              placeholder={
                hasInstrumentLink && instrumentSectorRef.current && !sector
                  ? translateSector(instrumentSectorRef.current)
                  : t('portfolio.sectorPlaceholder', { defaultValue: 'Select or enter sector' })
              }
              allowCustom={true}
              disabled={loading || isReadonly('sector')}
            />
          </View>

          {/* Industry Autocomplete - filtered by selected sector */}
          <View style={styles.fieldContainer}>
            <View style={styles.labelRow}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.industry', { defaultValue: 'Industry' })}
              </Text>
              {/* Show custom badge when linked and industry has value */}
              {hasInstrumentLink && industry ? (
                <View style={[styles.customBadge, { backgroundColor: theme.colors.primary }]}>
                  <Text style={[styles.customBadgeText, { color: theme.colors.textInverse }]}>
                    {t('portfolio.custom', { defaultValue: '自定义' })}
                  </Text>
                </View>
              ) : null}
            </View>
            <AutocompleteInput
              label=""
              value={industry}
              displayValue={industry ? translateIndustry(industry) : undefined}
              onChange={(value) => {
                setIndustry(value);
                // Mark as user-changed if different from auto-filled value
                if (autoFilledValues.industry && value !== autoFilledValues.industry) {
                  setUserChangedFields((prev) => new Set([...prev, 'industry']));
                }
              }}
              options={filteredIndustries}
              placeholder={
                hasInstrumentLink && instrumentIndustryRef.current && !industry
                  ? translateIndustry(instrumentIndustryRef.current)
                  : t('portfolio.industryPlaceholder', { defaultValue: 'Select or enter industry' })
              }
              allowCustom={true}
              disabled={loading || isReadonly('industry')}
            />
          </View>

          {/* Reset Override Button - only shown when linked and has user overrides */}
          {hasInstrumentLink && (sector || industry) ? (
            <TouchableOpacity
              style={[styles.resetButton, { borderColor: theme.colors.border }]}
              onPress={() => {
                // Clear local state only; actual update happens on submit
                if (sector) setSector('');
                if (industry) setIndustry('');
              }}
              activeOpacity={0.7}
              disabled={loading}
            >
              <Text style={[styles.resetButtonText, { color: theme.colors.textSecondary }]}>
                ↺ {t('portfolio.resetToInstrument', { defaultValue: 'Reset to Instrument Values' })}
              </Text>
            </TouchableOpacity>
          ) : null}

          {/* Currency Dropdown */}
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {t('portfolio.currency', { defaultValue: 'Currency' })} *
            </Text>
            <TouchableOpacity
              style={[
                styles.pickerInput,
                {
                  backgroundColor: isReadonly('currency')
                    ? theme.colors.background
                    : theme.colors.backgroundElevated,
                  borderColor: errors.currency ? theme.colors.error : theme.colors.border,
                  opacity: isReadonly('currency') ? 0.6 : 1,
                },
              ]}
              onPress={() => !isReadonly('currency') && setShowCurrencyPicker(true)}
              activeOpacity={0.7}
              disabled={loading || isReadonly('currency')}
            >
              <Text style={[styles.pickerText, { color: theme.colors.text }]}>{currency}</Text>
              {!isReadonly('currency') ? (
                <Text style={[styles.pickerArrow, { color: theme.colors.textSecondary }]}>▼</Text>
              ) : null}
            </TouchableOpacity>
            {errors.currency ? (
              <Text style={[styles.errorText, { color: theme.colors.error }]}>
                {errors.currency}
              </Text>
            ) : null}

            {showCurrencyPicker ? (
              <View
                style={[
                  styles.pickerModal,
                  { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
                ]}
              >
                <ScrollView style={styles.pickerScroll} keyboardShouldPersistTaps="handled">
                  {currencies.map((ccy) => (
                    <TouchableOpacity
                      key={ccy}
                      style={[styles.pickerOption, { borderBottomColor: theme.colors.border }]}
                      onPress={() => {
                        setCurrency(ccy);
                        setShowCurrencyPicker(false);
                        setErrors({ ...errors, currency: undefined });
                        // Mark as user-changed if different from auto-filled value
                        if (autoFilledValues.currency && ccy !== autoFilledValues.currency) {
                          setUserChangedFields((prev) => new Set([...prev, 'currency']));
                        }
                      }}
                    >
                      <Text
                        style={[
                          styles.pickerOptionText,
                          {
                            color: theme.colors.text,
                            fontWeight: currency === ccy ? 'bold' : 'normal',
                          },
                        ]}
                      >
                        {ccy}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <TouchableOpacity
                  style={[styles.pickerClose, { backgroundColor: theme.colors.background }]}
                  onPress={() => setShowCurrencyPicker(false)}
                >
                  <Text style={[styles.pickerCloseText, { color: theme.colors.text }]}>
                    {t('common.close', { defaultValue: 'Close' })}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }
);

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    formContainer: {
      borderRadius: theme.radii.lg,
      padding: 16,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
      minHeight: 200,
    },
    scrollContent: {
      flexGrow: 1,
      paddingHorizontal: 16,
      paddingTop: 8,
    },
    fieldContainer: {
      marginBottom: 10, // Reduced from 16 for compact layout
      zIndex: 10, // Ensure dropdowns appear above other elements
    },
    rowContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    halfField: {
      width: '48%',
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 6, // Reduced from 8 for compact layout
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 10, // Reduced from 12 for compact layout
      fontSize: theme.typography.fontSizeMd,
      minHeight: 42, // Reduced from 48 for compact layout
    },
    inputReadonly: {
      backgroundColor: 'transparent',
    },
    pickerInput: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: 10, // Reduced from 12 for compact layout
      minHeight: 42, // Reduced from 48 for compact layout
    },
    pickerText: {
      fontSize: theme.typography.fontSizeMd,
    },
    pickerArrow: {
      fontSize: 12,
    },
    pickerModal: {
      marginTop: 4,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      maxHeight: 200,
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
    },
    pickerScroll: {
      maxHeight: 160,
    },
    pickerOption: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
    },
    pickerOptionText: {
      fontSize: theme.typography.fontSizeMd,
    },
    pickerClose: {
      paddingVertical: 12,
      alignItems: 'center',
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    pickerCloseText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    inputError: {
      borderColor: theme.colors.error,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
    },
    readonlyInfo: {
      marginBottom: 4,
    },
    readonlyHintText: {
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic',
    },
    loadingContainer: {
      paddingVertical: 40,
      alignItems: 'center',
    },
    loadingText: {
      marginTop: 12,
      fontSize: theme.typography.fontSizeMd,
    },
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    customBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: theme.radii.sm,
    },
    customBadgeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    resetButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      marginTop: 8,
    },
    resetButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
  });
}
